import {Component, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import {FormArray, FormBuilder, FormGroup, Validators} from '@angular/forms';
import {MatDialog} from '@angular/material/dialog';
import {ShapefileDataDialogComponent} from './shapefile-data-dialog.component';
import {PushpinService} from '../../services/pushpin/pushpin.service';
import {CustomerArea} from '../../classes/customerarea.class';
import {Inspection} from '../../classes/inspection';
import {Pushpin} from '../../classes/pushpin.class';
import {PushpinPart} from '../../classes/pushpinpart.class';
import {CustomerAreaService} from '../../services/consumer-area/customer-area.service';
import {InspectionService} from '../../services/inspection.service';
import {MaterialService} from '../../services/material/material.service';
import {Material} from '../../classes/material.class';
import {DecorationsService} from '../../services/decorations/decorations.service';
import {Decoration} from '../../classes/decoration.class';
import {StatusService} from '../../services/status/status.service';
import {Status} from '../../classes/status.class';
import {PaintColorService} from '../../services/paintColor/paint-color.service';
import {PaintColor} from '../../classes/paintcolor.class';
import shp from 'shpjs';
import * as JSZip from 'jszip';
import {LocalStorage} from 'src/app/storage.class';
import * as turf from '@turf/turf';
import {clustersDbscan} from '@turf/clusters-dbscan';
import convertToWGS84 from 'rd-to-wgs84';
import {ConfirmDialogService} from '../../services/confirm-dialog-service/confirm-dialog.service';
import {CodaltComponent} from 'src/app/codalt.component';
import {from, of} from 'rxjs';
import {concatMap, map} from 'rxjs/operators';
import {LocationService} from '../../pushpin-detail/location-selector-dialog/location.service';
import {ShapefileImportService} from '../../services/shapefile-import/shapefile-import.service';
import {ShapefileImportExisting} from '../../classes/shapefile-import.class';
import {
    DecorationCandidate,
    enrichGisibRoadMarkings,
    findDecoration,
    GISIB_AMOUNT_PROPERTY,
    GISIB_FIGURATION_PROPERTY,
    GISIB_LENGTH_PROPERTY,
    GISIB_SURFACE_PROPERTY
} from './gisib-road-markings';

interface ShapefileCombination {
    name: string;
    files: { [filename: string]: JSZip.JSZipObject };
    hasShp: boolean;
    hasDbf: boolean;
    hasPrj: boolean;
    hasShx: boolean;
    selected: boolean;
}

interface SkippedGroup {
    label: string;
    count: number;
    values: { value: string; count: number }[];
}

interface ImportSummary {
    total: number;
    skipped: number;
    // Rows that were already imported into the inspection before, these are not imported again
    alreadyImported: number;
    groups: SkippedGroup[];
    // Imported rows without, or with a non-unique, reference (only when a reference column is mapped)
    referencesEmpty: number;
    referencesDuplicate: number;
}

/** New parts that are added to a pushpin that already exists in the inspection */
interface PushpinPartAppend {
    pushpinId: number;
    lat: number;
    lng: number;
    parts: PushpinPart[];
}

interface InitialPushpinPart {
    pushpinPart: PushpinPart;
    originalFeature: any;
    centerCoords: { lng: number; lat: number };
}

@Component({
    selector: 'app-pushpin-shape-import',
    templateUrl: './pushpin-shape-import.component.html',
    styleUrls: ['./pushpin-shape-import.component.scss']
})
export class PushpinShapeImportComponent extends CodaltComponent implements OnInit {

    importing: number = null;
    totalPushpins: number = 0;
    importForm: FormGroup;
    file: File;
    selectedFileName: string = '';
    geojson: any;
    features: any[] = [];
    shapefileProperties: string[] = [];
    shapefileCombinations: ShapefileCombination[] = [];
    selectedCombinations: ShapefileCombination[] = [];

    pushpinFields = [
        'description', 'reference', 'street', 'zipcode', 'housenumber', 'place'
    ];

    pushpinPartFields = [
        'external_reference', 'crow', 'description', 'customer_note', 'primer', 'blast', 'stake_out', 'traffic_controllers',
        'night', 'manual_burn', 'turbo_burn', 'exclusive_material', 'overgrown', 'amount', 'length', 'surface',
        'material_id', 'decoration_id', 'status_id', 'paint_color_id'
    ];

    mappableFields = [
        {name: 'material_id', label: 'Materiaal', items: 'materials', displayProperty: ['name']},
        {name: 'decoration_id', label: 'Figuratie', items: 'decorations', displayProperty: ['decoration_group', 'name']},
        {name: 'status_id', label: 'Status', items: 'statuses', displayProperty: ['name']},
        {name: 'paint_color_id', label: 'Kleur', items: 'paintColors', displayProperty: ['name']}
    ];

    pushpinFieldLabels = new Map<string, { name: string; description: string }>([
        ['description', { name: 'Beschrijving', description: '' }],
        ['reference', { name: 'Referentie', description: 'Een referentie van de opdrachtgever uit de shapefile. Niet verplicht.' }],
        ['street', { name: 'Straat', description: '' }],
        ['zipcode', { name: 'Postcode', description: '' }],
        ['housenumber', { name: 'Huisnummer', description: '' }],
        ['place', { name: 'Plaats', description: '' }]
    ]);

    pushpinPartFieldLabels = new Map<string, { name: string; description: string }>([
        ['external_reference', { name: 'Referentie deel', description: 'Unieke referentie van het object in de shapefile, bijvoorbeeld de GUID. Wordt bij het deel getoond, zodat het object terug te vinden is in het systeem van de opdrachtgever.' }],
        ['material_id', { name: 'Materiaal', description: '' }],
        ['decoration_id', { name: 'Figuratie', description: '' }],
        ['paint_color_id', { name: 'Kleur', description: '' }],
        ['amount', { name: 'Aantal', description: '' }],
        ['length', { name: 'Lengte', description: '' }],
        ['surface', { name: 'Oppervlakte', description: '' }],
        ['status_id', { name: 'Status', description: '' }],
        ['crow', { name: 'CROW', description: '' }],
        ['description', { name: 'Opmerkingen', description: '' }],
        ['customer_note', { name: 'Opmerking klant', description: '' }],
        ['primer', { name: 'Primer', description: '' }],
        ['blast', { name: 'Stralen', description: '' }],
        ['stake_out', { name: 'Uitzetten', description: '' }],
        ['traffic_controllers', { name: 'Verkeersregelaars', description: '' }],
        ['night', { name: 'Nacht', description: '' }],
        ['manual_burn', { name: 'Handmatig branden', description: '' }],
        ['turbo_burn', { name: 'Turbo branden', description: '' }],
        ['exclusive_material', { name: 'Exclusief materiaal', description: 'Het materiaal wordt niet gerekend maar eigenschappen als stralen en branden wel.' }],
        ['overgrown', { name: 'Overgroeide kanten', description: '' }]
    ]);

    // Map voor veelvoorkomende veldnamen naar interne veldnamen
    fieldNameMappings = new Map<string, string>([
        // Pushpin velden
        ['adres', 'street'],
        ['aantal', 'amount'],
        ['beschrijving', 'description'],
        ['beschrijvin', 'description'],
        ['beschrijvi', 'description'],
        ['crow', 'crow'],
        ['crowsysteem', 'crow'],
        ['decoratie', 'decoration_id'],
        ['decoratie_id', 'decoration_id'],
        ['distance', 'length'],
        ['excl_mater', 'exclusive_material'],
        ['exclusief_materiaal', 'exclusive_material'],
        ['f10', 'material_id'],
        ['f11', 'description'],
        ['f13', 'length'],
        ['f18', 'place'],
        ['f19', 'street'],
        ['f3', 'amount'],
        ['f4', 'paint_color_id'],
        ['f7', 'surface'],
        ['f9', 'decoration_id'],
        ['figuratie', 'decoration_id'],
        ['gemeente', 'place'],
        ['handbrand', 'manual_burn'],
        ['handmatig_branden', 'manual_burn'],
        ['huisnummer', 'housenumber'],
        ['item_naam', 'decoration_id'],
        ['klant_opm', 'customer_note'],
        ['klantnoti', 'customer_note'],
        ['kleur', 'paint_color_id'],
        ['kleur_id', 'paint_color_id'],
        ['lengte', 'length'],
        ['lengte_m', 'length'],
        ['materiaal', 'material_id'],
        ['materiaal_id', 'material_id'],
        ['nacht', 'night'],
        ['nachtwerk', 'night'],
        ['notitie', 'description'],
        ['nr', 'housenumber'],
        ['opmerking', 'description'],
        ['opmerkingen', 'description'],
        ['oppervlak', 'surface'],
        ['oppervlak_m2', 'surface'],
        ['oppervlakte', 'surface'],
        ['overgroeid', 'overgrown'],
        ['overgroeide_kanten', 'overgrown'],
        ['paint_color_id', 'paint_color_id'],
        ['plaats', 'place'],
        ['postcode', 'zipcode'],
        ['primer', 'primer'],
        ['primerwerk', 'primer'],
        ['guid', 'external_reference'],
        ['ref', 'reference'],
        ['referentie', 'reference'],
        ['stad', 'place'],
        ['soort', 'material_id'],
        ['stake_out', 'stake_out'],
        ['status', 'status_id'],
        ['status_id', 'status_id'],
        ['straat', 'street'],
        ['straatnaam', 'street'],
        ['stralen', 'blast'],
        ['straling', 'blast'],
        ['tekst', 'description'],
        ['turbobrand', 'turbo_burn'],
        ['turbo_branden', 'turbo_burn'],
        ['uitzetten', 'stake_out'],
        ['verfkleur', 'paint_color_id'],
        ['verkeersr', 'traffic_controllers'],
        ['verkeersregelaars', 'traffic_controllers'],
        // Afgeleide kolommen voor GISIB wegmarkeringen, zie gisib-road-markings.ts
        [GISIB_FIGURATION_PROPERTY.toLowerCase(), 'decoration_id'],
        [GISIB_LENGTH_PROPERTY.toLowerCase(), 'length'],
        [GISIB_AMOUNT_PROPERTY.toLowerCase(), 'amount'],
        [GISIB_SURFACE_PROPERTY.toLowerCase(), 'surface']
    ]);

    valueMappings = new Map<string, string>([
        ['wegenverf', 'verf'],
        ['haaientand_50_50', 'haaientand 0,50 * 0,50'],
        ['haaientand_50_70', 'haaientand 0,50 * 0,70']
    ]);

    automaticMappings = new Set<string>();
    // Voorgestelde figuraties per afgeleide figuratie-waarde van een GISIB wegmarkering shapefile
    gisibDecorationCandidates = new Map<string, DecorationCandidate[]>();
    // What the inspection already contains: features with a known reference are not imported again
    existingReferences = new Set<string>();
    existingPushpins: ShapefileImportExisting['pushpins'] = [];
    // Number of pushpins (and additions to existing pushpins) saved per request
    readonly saveChunkSize = 250;
    // Exactly this file was imported into the inspection before: its records ("layer|record") are not imported again
    fileHash: string = null;
    sameFileImport: ShapefileImportExisting['same_file_import'] = null;
    sameFileRecords = new Set<string>();
    // Regels die bij het importeren wegvallen door ontbrekende koppelingen, getoond vóór het importeren
    importSummary: ImportSummary = null;
    readonly importSummaryMaxValues = 10;
    // Groepen in de samenvatting waarvan alle waarden getoond worden
    expandedSummaryGroups = new Set<string>();
    // Voortgang van inlezen en herkennen van de shapefile (null wanneer er niets bezig is)
    detection: { label: string; progress: number } = null;
    customerAreas: CustomerArea[] = [];
    inspections: Inspection[] = [];
    materials: Material[] = [];
    decorations: Decoration[] = [];
    statuses: Status[] = [];
    paintColors: PaintColor[] = [];
    fieldMappings = new Map<string, Map<string, number>>();

    availableDefaultFields: {
        name: string,
        label: string,
        items: string,
        displayProperty: string[],
        helpText: string
    }[] = [];

    // Map voor het ophalen van field items arrays
    // Opties voor de selects per items-lijst, met een kant-en-klaar label. Via [items] maakt ng-select de opties
    // pas aan bij openen, in plaats van honderden ng-option componenten per select (traag bij veel koppelingen)
    fieldOptionsMap = new Map<string, { id: number; label: string }[]>();

    fieldItemsMap = new Map<string, any[]>([
        ['materials', this.materials],
        ['decorations', this.decorations],
        ['statuses', this.statuses],
        ['paintColors', this.paintColors]
    ]);

    constructor(
        private router: Router,
        private fb: FormBuilder,
        private pushpinService: PushpinService,
        private customerAreaService: CustomerAreaService,
        private inspectionService: InspectionService,
        private materialService: MaterialService,
        private decorationsService: DecorationsService,
        private statusService: StatusService,
        private paintColorService: PaintColorService,
        private dialog: MatDialog,
        private confirmDialogService: ConfirmDialogService,
        private locationService: LocationService,
        private shapefileImportService: ShapefileImportService
    ) {
        super();
        this.initializeForm();
    }

    ngOnInit() {
        this.loadCustomerAreas();
        this.loadMaterials();
        this.loadDecorations();
        this.loadStatuses();
        this.loadPaintColors();
        this.updateAvailableDefaultFields();
    }

    private initializeForm() {
        this.importForm = this.fb.group({
            customer_area_id: ['', Validators.required],
            inspection_id: ['', Validators.required],
            autoFillLocationFields: [false],
            defaultMaterialId: [''],
            defaultDecorationId: [''],
            defaultPaintColorId: [''],
            pushpinMappings: this.fb.array([]),
            pushpinPartMappings: this.fb.array([])
        });
        this.initializeMappingsFormArray();
        // Kolomkoppelingen en standaardwaarden bepalen welke regels overgeslagen worden
        this.subscriptions.add(this.importForm.valueChanges.subscribe(() => this.updateImportSummary()));
        this.importForm.get('autoFillLocationFields')?.valueChanges.subscribe(value => {
            ['street', 'zipcode', 'housenumber', 'place'].forEach(fieldName => {
                const index = this.pushpinFields.indexOf(fieldName);
                if (index !== -1) {
                    const control = this.pushpinMappingsFormArray.at(index);
                    if (value) {
                        control.disable();
                    } else {
                        control.enable();
                    }
                }
            });
        });
    }

    get pushpinMappingsFormArray(): FormArray {
        return this.importForm.get('pushpinMappings') as FormArray;
    }

    get pushpinPartMappingsFormArray(): FormArray {
        return this.importForm.get('pushpinPartMappings') as FormArray;
    }

    // Helper methods to check if mappings are set
    get hasMaterialMapping(): boolean {
        const materialIdIndex = this.pushpinPartFields.indexOf('material_id');
        return materialIdIndex >= 0 && !!this.pushpinPartMappingsFormArray.at(materialIdIndex)?.value;
    }

    get hasDecorationMapping(): boolean {
        const decorationIdIndex = this.pushpinPartFields.indexOf('decoration_id');
        return decorationIdIndex >= 0 && !!this.pushpinPartMappingsFormArray.at(decorationIdIndex)?.value;
    }

    get hasPaintColorMapping(): boolean {
        const paintColorIdIndex = this.pushpinPartFields.indexOf('paint_color_id');
        return paintColorIdIndex >= 0 && !!this.pushpinPartMappingsFormArray.at(paintColorIdIndex)?.value;
    }

    // Private method to update the cached available default fields
    private updateAvailableDefaultFields(): void {
        const fields = [];

        if (!this.hasMaterialMapping) {
            fields.push({
                name: 'defaultMaterialId',
                label: 'Standaard Materiaal',
                items: 'materials',
                displayProperty: ['name'],
                helpText: 'Deze waarde wordt gebruikt omdat er geen materiaal koppeling is ingesteld'
            });
        }

        if (!this.hasDecorationMapping) {
            fields.push({
                name: 'defaultDecorationId',
                label: 'Standaard Figuratie',
                items: 'decorations',
                displayProperty: ['decoration_group', 'name'],
                helpText: 'Deze waarde wordt gebruikt omdat er geen figuratie koppeling is ingesteld'
            });
        }

        if (!this.hasPaintColorMapping) {
            fields.push({
                name: 'defaultPaintColorId',
                label: 'Standaard Kleur',
                items: 'paintColors',
                displayProperty: ['name'],
                helpText: 'Deze waarde wordt gebruikt omdat er geen kleur koppeling is ingesteld'
            });
        }

        this.availableDefaultFields = fields;
    }

    private initializeMappingsFormArray() {
        // Clear existing mappings
        while (this.pushpinMappingsFormArray.length > 0) {
            this.pushpinMappingsFormArray.removeAt(0);
        }
        while (this.pushpinPartMappingsFormArray.length > 0) {
            this.pushpinPartMappingsFormArray.removeAt(0);
        }

        // Add mapping controls for each pushpin field
        this.pushpinFields.forEach(field => {
            this.pushpinMappingsFormArray.push(this.fb.control(''));
        });

        // Add mapping controls for each pushpin part field
        this.pushpinPartFields.forEach(field => {
            this.pushpinPartMappingsFormArray.push(this.fb.control(''));
        });

        this.updateAvailableDefaultFields();
    }

    private loadCustomerAreas() {
        this.customerAreaService.getAllCustomerAreas().then(customerAreas => {
            this.customerAreas = customerAreas;
        });
    }

    private loadMaterials() {
        this.materialService.getMaterials().then(materials => {
            this.materials = materials;
            this.setFieldItems('materials', materials);
        });
    }

    private loadDecorations() {
        this.decorationsService.getList().then(decorations => {
            this.decorations = decorations;
            this.setFieldItems('decorations', decorations);
        });
    }

    private loadStatuses() {
        const inspectionTypeId = this.getSelectedInspectionTypeId();
        this.statusService.getStatuses(inspectionTypeId).then(statuses => {
            this.statuses = statuses;
            this.setFieldItems('statuses', statuses);
        });
    }

    private loadPaintColors() {
        this.paintColorService.getList().then(paintColors => {
            this.paintColors = paintColors;
            this.setFieldItems('paintColors', paintColors);
        });
    }

    private setFieldItems(itemsKey: string, items: any[]) {
        this.fieldItemsMap.set(itemsKey, items);
        const displayProperty = this.mappableFields.find(field => field.items === itemsKey)?.displayProperty ?? ['name'];
        this.fieldOptionsMap.set(itemsKey, items.map(item => ({
            id: item.id,
            label: displayProperty.map(prop => item[prop]).filter(Boolean).join(' ')
        })));
    }

    private getSelectedInspectionTypeId(): number {
        const inspectionId = this.importForm.get('inspection_id')?.value;
        if (inspectionId) {
            const selectedInspection = this.inspections.find(ins => ins.id === inspectionId);
            return selectedInspection?.inspection_type_id || 1;
        }
        return 1; // Default inspection type
    }

    onCustomerAreaChange() {
        const customerAreaId = this.importForm.get('customer_area_id')?.value;
        if (customerAreaId) {
            const selectedCustomerArea = this.customerAreas.find(ca => ca.id === customerAreaId);
            if (selectedCustomerArea) {
                // Check if inspections are already loaded
                if (selectedCustomerArea.inspections && selectedCustomerArea.inspections.length > 0) {
                    this.inspections = selectedCustomerArea.inspections;
                } else {
                    // If inspections are not loaded, try to load them
                    this.loadInspectionsForCustomerArea(selectedCustomerArea);
                }
            }
            // Reset inspection selection
            this.importForm.get('inspection_id')?.setValue('');
        } else {
            this.inspections = [];
            this.importForm.get('inspection_id')?.setValue('');
        }
        // Reload statuses when customer area changes
        this.loadStatuses();
    }

    loadInspectionsForCustomerArea(customerArea: CustomerArea) {
        if (customerArea && customerArea.id) {
            this.inspectionService.getInspections(customerArea.id).subscribe(
                response => {
                    this.inspections = response.data || [];
                    customerArea.inspections = response.data || [];
                    // Reload statuses when inspections are loaded
                    this.loadStatuses();
                },
                err => {
                    console.error('Error loading inspections:', err);
                    this.inspections = [];
                }
            );
        }
    }

    onFileSelected(event: any) {
        this.file = event.target.files[0];
        if (this.file) {
            this.selectedFileName = this.file.name;
            if (this.file.name.toLowerCase().endsWith('.zip')) {
                this.readZipFile();
            } else {
                this.confirmDialogService.confirm('Ongeldig bestand', 'Alleen ZIP bestanden met shapefiles worden ondersteund.').then(() => {
                });
            }
        }
    }

    private async readZipFile() {
        const reader = new FileReader();
        reader.onload = async (e) => {
            try {
                const buffer = e.target.result as ArrayBuffer;
                this.fileHash = await this.shapefileImportService.hashFile(buffer);
                const zip = await JSZip.loadAsync(buffer);

                // Group files by shapefile name (without extension)
                const fileGroups: { [name: string]: { [filename: string]: JSZip.JSZipObject } } = {};

                zip.forEach((relativePath, file) => {
                    if (!file.dir) {
                        const filename = relativePath.split('/').pop() || '';
                        const nameWithoutExt = filename.replace(/\.(shp|dbf|prj|shx)$/i, '');

                        if (!fileGroups[nameWithoutExt]) {
                            fileGroups[nameWithoutExt] = {};
                        }
                        fileGroups[nameWithoutExt][filename] = file;
                    }
                });

                // Create shapefile combinations
                this.shapefileCombinations = Object.keys(fileGroups).map(name => {
                    const files = fileGroups[name];
                    const filenames = Object.keys(files);

                    return {
                        name: name,
                        files: files,
                        hasShp: filenames.some(f => f.toLowerCase().endsWith('.shp')),
                        hasDbf: filenames.some(f => f.toLowerCase().endsWith('.dbf')),
                        hasPrj: filenames.some(f => f.toLowerCase().endsWith('.prj')),
                        hasShx: filenames.some(f => f.toLowerCase().endsWith('.shx')),
                        selected: false
                    };
                }).filter(combo => combo.hasShp && combo.hasDbf); // Only include valid combinations

                setTimeout(() => {
                    this.selectAllCombinations();
                });

                if (this.shapefileCombinations.length === 0) {
                    this.confirmDialogService.confirm('Geen geldige shapefiles', 'Geen geldige shapefile combinaties gevonden in de ZIP. Elke combinatie moet minimaal een .shp en .dbf bestand bevatten.').then(() => {
                    }).catch(() => {
                    });
                    return;
                }

                // Reset features and properties
                this.features = [];
                this.shapefileProperties = [];
                this.selectedCombinations = [];

            } catch (err) {
                console.error('Error reading ZIP file:', err);
                this.confirmDialogService.confirm('Fout bij lezen ZIP bestand', 'Fout bij het lezen van ZIP bestand: ' + (err as Error).message).then(() => {
                }).catch(() => {
                });
            }
        };
        reader.readAsArrayBuffer(this.file);
    }

    async processSelectedCombinations() {
        if (this.selectedCombinations.length === 0) {
            this.confirmDialogService.confirm('Geen selectie', 'Selecteer minimaal één shapefile combinatie.').then(() => {
            }).catch(() => {
            });
            return;
        }
        this.importForm.get('customer_area_id').disable();
        this.importForm.get('inspection_id').disable();


        try {
            const features = [];
            this.features = [];
            this.shapefileProperties = [];

            // Inlezen telt voor de eerste helft van de voortgang, de figuratie-herkenning voor de rest
            const combinations = this.selectedCombinations;
            for (const [index, combination] of combinations.entries()) {
                await this.setDetectionProgress(`Shapefile inlezen: ${combination.name} (${index + 1}/${combinations.length})`, index / combinations.length * 50);
                const geojson = await this.parseShapefileCombination(combination);
                if (geojson && geojson.features) {
                    // shpjs returns the features in DBF record order; remembered so the shapefile can be updated on export
                    geojson.features.forEach((feature, record) => feature.source = {layer: combination.name, record});
                    features.push(...geojson.features);
                }
            }

            if (features.length > 0) {
                this.gisibDecorationCandidates = await enrichGisibRoadMarkings(features, this.decorations, (done, total) => {
                    this.detection = {label: `Markeringen herkennen (${done}/${total})`, progress: 50 + done / total * 45};
                });
                await this.setDetectionProgress('Koppelingen bepalen', 95);
                await this.loadExisting();
                this.shapefileProperties = Object.keys(features[0].properties);
                this.features = features;
                this.applyAutomaticFieldMappings(); // Automatische mappings toepassen
                this.initializeFieldMappings();
            }

        } catch (err) {
            console.error('Error processing shapefile combinations:', err);
            this.confirmDialogService.confirm('Fout bij verwerken shapefiles', 'Fout bij het verwerken van shapefile combinaties: ' + (err as Error).message).then(() => {
            }).catch(() => {
            });
        } finally {
            this.detection = null;
        }
    }

    /** Loads what the inspection already contains, so a new import of the same shapefile only adds the missing parts */
    private async loadExisting() {
        const existing = await this.shapefileImportService.getExisting(this.importForm.get('inspection_id').value, this.fileHash).toPromise();
        this.existingReferences = new Set((existing?.references ?? []).map(reference => String(reference).trim()));
        this.existingPushpins = existing?.pushpins ?? [];
        this.sameFileImport = existing?.same_file_import ?? null;
        this.sameFileRecords = new Set(existing?.same_file_records ?? []);
    }

    /** The unique reference of a feature from the mapped reference column, or null */
    private referenceOf(feature: any): string | null {
        const referenceProp = this.pushpinPartMappingsFormArray.at(this.pushpinPartFields.indexOf('external_reference'))?.value;
        const reference = referenceProp ? feature.properties?.[referenceProp] : null;
        return reference === null || reference === undefined || reference === '' || Number.isNaN(reference) ? null : String(reference).trim();
    }

    /** Feature that was already imported into the inspection before: same reference, or same record of the same file */
    private isAlreadyImported(feature: any): boolean {
        const reference = this.referenceOf(feature);
        return (!!reference && this.existingReferences.has(reference))
            || (!!feature.source && this.sameFileRecords.has(`${feature.source.layer}|${feature.source.record}`));
    }

    /** Toont de stap en geeft de browser de kans de voortgang te tekenen voordat er zwaar rekenwerk volgt. */
    private async setDetectionProgress(label: string, progress: number) {
        this.detection = {label, progress};
        // Wachten tot na het volgende frame, met een timeout omdat requestAnimationFrame in een achtergrondtab niet afgaat
        await new Promise(resolve => {
            requestAnimationFrame(() => setTimeout(resolve));
            setTimeout(resolve, 100);
        });
    }

    private async parseShapefileCombination(combination: ShapefileCombination): Promise<any> {
        try {
            console.log('Starting to parse combination:', combination.name);
            console.log('Available files:', Object.keys(combination.files));

            // Try a simpler approach - just pass the shp and dbf files
            const shpFile = Object.keys(combination.files).find(f => f.toLowerCase().endsWith('.shp'));
            const dbfFile = Object.keys(combination.files).find(f => f.toLowerCase().endsWith('.dbf'));

            if (!shpFile || !dbfFile) {
                throw new Error('Missing required .shp or .dbf file');
            }

            // Load the files
            const shpBuffer = await combination.files[shpFile].async('arraybuffer');
            const dbfBuffer = await combination.files[dbfFile].async('arraybuffer');

            // Try different approaches for shpjs
            let result;

            try {
                // Approach 1: Simple object with ArrayBuffers
                const shapefileData = {
                    shp: shpBuffer,
                    dbf: dbfBuffer
                };
                console.log('Trying approach 1 with ArrayBuffers');
                result = await shp(shapefileData);
            } catch (e1) {
                console.warn('Approach 1 failed:', e1.message);

                try {
                    // Approach 2: Convert to Uint8Arrays
                    const shapefileData = {
                        shp: new Uint8Array(shpBuffer),
                        dbf: new Uint8Array(dbfBuffer)
                    };
                    console.log('Trying approach 2 with Uint8Arrays');
                    result = await shp(shapefileData);
                } catch (e2) {
                    console.warn('Approach 2 failed:', e2.message);

                    // Approach 3: Try just the SHP file alone
                    console.log('Trying approach 3: SHP file only');
                    result = await shp(shpBuffer);
                }
            }

            console.log('Shapefile parsed successfully, features count:', result?.features?.length || 0);
            return result;

        } catch (error) {
            console.error('Error in parseShapefileCombination:', error);
            console.error('Error details:', {
                message: error.message,
                stack: error.stack,
                combination: combination.name,
                files: Object.keys(combination.files)
            });
            throw new Error(`Failed to parse shapefile combination "${combination.name}": ${error.message}`);
        }
    }

    onSelectionChange(event: any) {
        this.selectedCombinations = event.source.selectedOptions.selected.map(option => option.value);

        this.shapefileCombinations.forEach(combination => {
            combination.selected = this.selectedCombinations.includes(combination);
        });
    }

    selectAllCombinations() {
        this.selectedCombinations = [...this.shapefileCombinations];
        this.shapefileCombinations.forEach(combination => {
            combination.selected = true;
        });
    }

    deselectAllCombinations() {
        this.selectedCombinations = [];
        this.shapefileCombinations.forEach(combination => {
            combination.selected = false;
        });
    }

    private initializeFieldMappings() {
        this.mappableFields.map(mf => mf.name).forEach(fieldName => {
            const fieldIndex = this.pushpinPartFields.indexOf(fieldName);
            const fieldProp = this.pushpinPartMappingsFormArray.at(fieldIndex)?.value;

            if (fieldProp && this.features) {
                // Find unique values in shapefile for this field
                const uniqueValues = new Set<string>();
                this.features.forEach(feature => {
                    const value = feature.properties?.[fieldProp];
                    // Lege waarden ook tonen, zodat bijv. een lege kleur alsnog gekoppeld kan worden
                    if (value !== undefined && value !== null && !Number.isNaN(value)) {
                        uniqueValues.add(value);
                    }
                });

                // Create mapping for this field
                const fieldMapping = new Map<string, number>();
                uniqueValues.forEach(value => {
                    fieldMapping.set(value, null);
                });

                this.fieldMappings.set(fieldName, fieldMapping);
            } else {
                // Clear mapping if no property is selected
                this.fieldMappings.delete(fieldName);
            }
        });

        // Apply automatic value mappings for special fields
        this.applyAutomaticValueMappings();
        this.updateImportSummary();
    }

    /**
     * Telt de regels die createInitialPushpinParts zal overslaan omdat figuratie, materiaal of kleur ontbreekt,
     * gegroepeerd per veld en per shapefile waarde, zodat de gebruiker de koppelingen eerst kan aanvullen.
     */
    private updateImportSummary() {
        if (!this.features.length) {
            this.importSummary = null;
            return;
        }

        const requiredFields = [
            {field: 'decoration_id', label: 'Figuratie', defaultControl: 'defaultDecorationId'},
            {field: 'material_id', label: 'Materiaal', defaultControl: 'defaultMaterialId'},
            {field: 'paint_color_id', label: 'Kleur', defaultControl: 'defaultPaintColorId'}
        ].map(required => ({
            ...required,
            shpProp: this.pushpinPartMappingsFormArray.at(this.pushpinPartFields.indexOf(required.field))?.value,
            defaultId: this.importForm.get(required.defaultControl)?.value,
            mapping: this.fieldMappings.get(required.field),
            missing: new Map<string, number>()
        }));
        const withoutGeometry = new Map<string, number>();

        const referenceProp = this.pushpinPartMappingsFormArray.at(this.pushpinPartFields.indexOf('external_reference'))?.value;
        const referenceCounts = new Map<string, number>();
        let referencesEmpty = 0;

        let skipped = 0;
        let alreadyImported = 0;
        this.features.forEach(feature => {
            if (this.isAlreadyImported(feature)) {
                alreadyImported++;
                return;
            }
            let isSkipped = false;
            if (!feature.geometry) {
                withoutGeometry.set('Geen locatie', (withoutGeometry.get('Geen locatie') ?? 0) + 1);
                isSkipped = true;
            }
            requiredFields.forEach(required => {
                const value = required.shpProp ? feature.properties?.[required.shpProp] : undefined;
                // Zonder kolomkoppeling wordt de standaardwaarde gebruikt (zie createInitialPushpinParts)
                const hasId = required.shpProp ? value !== undefined && !!required.mapping?.get(value) : !!required.defaultId;
                if (!hasId) {
                    const key = !required.shpProp
                        ? 'Geen koppeling of standaardwaarde'
                        : value === null || value === undefined || value === '' || Number.isNaN(value) ? '(leeg)' : String(value);
                    required.missing.set(key, (required.missing.get(key) ?? 0) + 1);
                    isSkipped = true;
                }
            });
            if (isSkipped) {
                skipped++;
            } else if (referenceProp) {
                const reference = feature.properties?.[referenceProp];
                if (reference === null || reference === undefined || reference === '' || Number.isNaN(reference)) {
                    referencesEmpty++;
                } else {
                    referenceCounts.set(String(reference), (referenceCounts.get(String(reference)) ?? 0) + 1);
                }
            }
        });

        const toGroup = (label: string, missing: Map<string, number>): SkippedGroup => ({
            label,
            count: [...missing.values()].reduce((sum, count) => sum + count, 0),
            values: [...missing].map(([value, count]) => ({value, count})).sort((a, b) => b.count - a.count)
        });
        this.importSummary = {
            total: this.features.length - alreadyImported,
            skipped,
            alreadyImported,
            groups: [
                ...requiredFields.map(required => toGroup(required.label, required.missing)),
                toGroup('Locatie', withoutGeometry)
            ].filter(group => group.count > 0),
            referencesEmpty,
            referencesDuplicate: [...referenceCounts.values()].filter(count => count > 1).reduce((sum, count) => sum + count, 0)
        };
    }

    trackByKey = (index: number, mapping: { key: string }) => mapping.key;

    toggleSummaryGroup(label: string) {
        if (!this.expandedSummaryGroups.delete(label)) {
            this.expandedSummaryGroups.add(label);
        }
    }

    import() {
        if (!this.importSummary?.skipped) {
            this.runImport();
            return;
        }
        this.confirmDialogService.confirm(
            'Regels overslaan',
            `${this.importSummary.skipped} van de ${this.importSummary.total} regels worden niet geïmporteerd, omdat de figuratie, ` +
            'het materiaal of de kleur niet gekoppeld is. Zie de samenvatting onderaan de pagina.<br><br>Toch importeren?',
            'Importeren',
            'Annuleren'
        ).then(() => this.runImport(), () => {
            // Annuleren: niets importeren
        });
    }

    /** Fouten tijdens het voorbereiden van de import tonen in plaats van met een hangende knop te blijven staan. */
    private runImport() {
        try {
            this.importFeatures();
        } catch (error) {
            console.error('Error importing shapefile:', error);
            this.importing = null;
            this.totalPushpins = 0;
            this.confirmDialogService.confirm('Fout bij importeren', 'Fout bij importeren: ' + (error as Error).message, 'Sluiten', null).catch(() => {
            });
        }
    }

    private importFeatures() {

        const customerAreaId = this.importForm.controls.customer_area_id.value;
        const inspectionId = this.importForm.controls.inspection_id.value;
        const autoFillLocationFields = this.importForm.controls.autoFillLocationFields.value;

        const selectedCustomerArea = this.customerAreas.find(ca => ca.id === customerAreaId);
        const selectedInspection = this.inspections.find(ins => ins.id === inspectionId);

        this.importing = 0;

        // Step 1: Convert coordinates and create one pushpin part per feature with mapped properties.
        // Parts are not merged, so every part stays 1-on-1 with a feature of the delivered shapefile.
        const initialPushpinParts = this.createInitialPushpinParts(selectedCustomerArea, selectedInspection);

        // Step 2: Parts close to a pushpin that already exists in the inspection (earlier import) are added to it
        const {appends, remaining} = this.appendToExistingPushpins(initialPushpinParts);

        // Step 3: Group the other PushpinParts into new Pushpins (1.5m)
        const pushpins = this.clusterIntoPushpins(remaining, selectedCustomerArea);

        this.totalPushpins = pushpins.length + appends.length;

        if (this.totalPushpins > 0) {
            // Eerst het originele shapefile bewaren, zodat het later bijgewerkt teruggeleverd kan worden
            const referenceField = this.pushpinPartMappingsFormArray.at(this.pushpinPartFields.indexOf('external_reference'))?.value;
            this.shapefileImportService.upload(this.file, selectedInspection.id, referenceField).subscribe({
                next: shapefileImport => {
                    pushpins.forEach(pushpin => pushpin.pushpin_parts.forEach(part => part.shapefile_import_id = shapefileImport.id));
                    appends.forEach(append => append.parts.forEach(part => part.shapefile_import_id = shapefileImport.id));
                    if (autoFillLocationFields) {
                        // Haal locaties op voor alle nieuwe pushpins sequentieel
                        this.fetchLocationsForPushpins(pushpins, selectedCustomerArea, selectedInspection, appends);
                    } else {
                        // Ga direct door naar opslaan
                        this.savePushpins(pushpins, selectedCustomerArea, selectedInspection, appends);
                    }
                },
                error: error => {
                    console.error('Error uploading shapefile:', error);
                    this.importing = null;
                    this.totalPushpins = 0;
                    this.confirmDialogService.confirm(
                        'Fout bij importeren',
                        'Het shapefile kon niet op de server worden opgeslagen, er is niets geïmporteerd. ' + (error?.message ?? ''),
                        'Sluiten', null).catch(() => {
                    });
                }
            });
        } else {
            const message = this.importSummary?.alreadyImported > 0
                ? 'Er zijn geen nieuwe delen om te importeren. Alle regels met een koppeling staan al in deze schouwronde.'
                : 'Fout bij het importeren, de ingestelde koppelingen resulteren niet in te importeren punaises. Controleer of alle koppelingen ingesteld zijn.';
            this.confirmDialogService.confirm('Fout bij importeren', message, 'Sluiten', null).then(() => {
                this.importing = null;
            }).catch(() => {
                this.importing = null;
            });
        }
    }

    /**
     * Adds parts to the nearest pushpin of an earlier import within 1.5 m. Lines are lengthmarks with their own
     * geometry, those always become a new pushpin.
     */
    private appendToExistingPushpins(parts: InitialPushpinPart[]): { appends: PushpinPartAppend[], remaining: InitialPushpinPart[] } {
        const candidates = this.existingPushpins
            .filter(pushpin => !pushpin.lengthmark)
            .map(pushpin => ({id: pushpin.id, lat: +pushpin.lat, lng: +pushpin.lng, point: turf.point([+pushpin.lng, +pushpin.lat])}));
        // 1.5 m is far less than 0.0001 degree (~7 m east-west here), used to skip most candidates without calculating a distance
        const roughDegrees = 0.0001;
        const appendsByPushpinId = new Map<number, PushpinPartAppend>();
        const remaining: InitialPushpinPart[] = [];

        parts.forEach(part => {
            const isLine = part.originalFeature.geometry.type.endsWith('LineString');
            const partPoint = turf.point([part.centerCoords.lng, part.centerCoords.lat]);
            let nearest: { id: number, lat: number, lng: number } = null;
            let nearestDistance = 1.5;
            if (!isLine) {
                candidates.forEach(pushpin => {
                    if (Math.abs(pushpin.lat - part.centerCoords.lat) > roughDegrees || Math.abs(pushpin.lng - part.centerCoords.lng) > roughDegrees) {
                        return;
                    }
                    const distance = turf.distance(partPoint, pushpin.point, {units: 'meters'});
                    if (distance <= nearestDistance) {
                        nearest = pushpin;
                        nearestDistance = distance;
                    }
                });
            }
            if (nearest) {
                if (!appendsByPushpinId.has(nearest.id)) {
                    appendsByPushpinId.set(nearest.id, {pushpinId: nearest.id, lat: nearest.lat, lng: nearest.lng, parts: []});
                }
                appendsByPushpinId.get(nearest.id).parts.push(part.pushpinPart);
            } else {
                remaining.push(part);
            }
        });

        return {appends: [...appendsByPushpinId.values()], remaining};
    }

    private createInitialPushpinParts(selectedCustomerArea: CustomerArea, selectedInspection: Inspection): InitialPushpinPart[] {
        const pushpinParts: InitialPushpinPart[] = [];

        this.features.filter(f => !!f.geometry && !this.isAlreadyImported(f)).forEach(sourceFeature => {
            // Every feature becomes exactly one part, so it stays 1-on-1 with the delivered shapefile. For a
            // MultiLineString the longest line is used as the geometry of the lengthmark.
            const feature = sourceFeature.geometry.type === 'MultiLineString' ? this.longestLineString(sourceFeature) : sourceFeature;
            if (!feature.geometry) {
                return;
            }
            const pushpinPart = new PushpinPart();
            pushpinPart.customer_area_id = selectedCustomerArea.id;
            pushpinPart.inspection_id = selectedInspection.id;
            pushpinPart.user_id = LocalStorage.getUser().id;
            pushpinPart.status_id = 1; // Default status
            pushpinPart.shapefile_layer = feature.source?.layer ?? null;
            pushpinPart.shapefile_record = feature.source?.record ?? null;

            // Map all pushpin part fields
            this.pushpinPartFields.forEach((appField, index) => {
                const shpProp = this.pushpinPartMappingsFormArray.at(index)?.value;
                if (shpProp && feature.properties[shpProp] !== undefined) {
                    this.mapFieldValue(appField, feature.properties[shpProp], pushpinPart);
                }
            });

            // Convert coordinates and store them for clustering
            let centerCoords: { lng: number, lat: number };
            if (feature.geometry.type === 'Point') {
                centerCoords = this.convertCoordinatesIfNeeded(
                    feature.geometry.coordinates[0],
                    feature.geometry.coordinates[1]
                );
            } else if (feature.geometry.type === 'LineString') {
                // Use center of line for clustering
                try {
                    const lineString = turf.lineString(feature.geometry.coordinates);
                    const center = turf.center(lineString);
                    centerCoords = this.convertCoordinatesIfNeeded(
                        center.geometry.coordinates[0],
                        center.geometry.coordinates[1]
                    );
                } catch (error) {
                    // Fallback to midpoint
                    const startCoords = this.convertCoordinatesIfNeeded(
                        feature.geometry.coordinates[0][0],
                        feature.geometry.coordinates[0][1]
                    );
                    const endCoords = this.convertCoordinatesIfNeeded(
                        feature.geometry.coordinates[feature.geometry.coordinates.length - 1][0],
                        feature.geometry.coordinates[feature.geometry.coordinates.length - 1][1]
                    );
                    centerCoords = {
                        lng: (startCoords.lng + endCoords.lng) / 2,
                        lat: (startCoords.lat + endCoords.lat) / 2
                    };
                }
            } else if (feature.geometry.type === 'Polygon') {
                try {
                    const polygon = turf.polygon(feature.geometry.coordinates);
                    const center = turf.center(polygon);
                    centerCoords = this.convertCoordinatesIfNeeded(
                        center.geometry.coordinates[0],
                        center.geometry.coordinates[1]
                    );
                } catch (error) {
                    // Fallback: first coordinate (geometry may be empty/degenerate)
                    const first = this.findFirstCoordinate(feature.geometry.coordinates);
                    if (first) {
                        centerCoords = this.convertCoordinatesIfNeeded(first[0], first[1]);
                    }
                }
            } else if (feature.geometry.type === 'MultiPolygon') {
                try {
                    const multiPolygon = turf.multiPolygon(feature.geometry.coordinates);
                    const center = turf.center(multiPolygon);
                    centerCoords = this.convertCoordinatesIfNeeded(
                        center.geometry.coordinates[0],
                        center.geometry.coordinates[1]
                    );
                } catch (error) {
                    // Fallback: first coordinate (geometry may be empty/degenerate)
                    const first = this.findFirstCoordinate(feature.geometry.coordinates);
                    if (first) {
                        centerCoords = this.convertCoordinatesIfNeeded(first[0], first[1]);
                    }
                }
            }

            // Skip features for which no usable coordinate could be determined
            if (!centerCoords) {
                return;
            }

            // Store the pushpin part with its original feature and center coordinates
            pushpinParts.push({
                pushpinPart: pushpinPart,
                originalFeature: feature,
                centerCoords: centerCoords
            });
        });

        // Apply default values for missing material_id, decoration_id, and paint_color_id only when no mapping is set
        const defaultMaterialId = this.importForm.get('defaultMaterialId')?.value;
        const defaultDecorationId = this.importForm.get('defaultDecorationId')?.value;
        const defaultPaintColorId = this.importForm.get('defaultPaintColorId')?.value;

        pushpinParts.forEach(pp => {
            // Amount defaults to 1 when missing or not a number (e.g. null, '', 'abc', NaN)
            const amount = parseFloat(pp.pushpinPart.amount as any);
            pp.pushpinPart.amount = Number.isFinite(amount) ? amount : 1;
            if (!this.hasMaterialMapping &&!pp.pushpinPart.material_id && defaultMaterialId) {
                pp.pushpinPart.material_id = defaultMaterialId;
            }
            if (!this.hasDecorationMapping && !pp.pushpinPart.decoration_id && defaultDecorationId) {
                pp.pushpinPart.decoration_id = defaultDecorationId;
            }
            if (!this.hasPaintColorMapping && !pp.pushpinPart.paint_color_id && defaultPaintColorId) {
                pp.pushpinPart.paint_color_id = defaultPaintColorId;
            }
        });

        return pushpinParts.filter(pp => !!pp.pushpinPart.decoration_id && !!pp.pushpinPart.material_id && !!pp.pushpinPart.paint_color_id);
    }

    /** The MultiLineString feature as a LineString feature with its longest line */
    private longestLineString(feature: any): any {
        const lines: number[][][] = feature.geometry.coordinates.filter(line => line?.length > 1);
        const lineLength = (line: number[][]) => line.slice(1).reduce((sum, point, i) => sum + Math.hypot(point[0] - line[i][0], point[1] - line[i][1]), 0);
        const longest = lines.reduce((best, line) => !best || lineLength(line) > lineLength(best) ? line : best, null);
        return {...feature, geometry: longest ? {type: 'LineString', coordinates: longest} : null};
    }

    /** Recursively finds the first [x, y] position in a (possibly empty) GeoJSON coordinates array. */
    private findFirstCoordinate(coordinates: any): [number, number] | null {
        if (!Array.isArray(coordinates)) {
            return null;
        }
        if (coordinates.length >= 2 && typeof coordinates[0] === 'number' && typeof coordinates[1] === 'number') {
            return [coordinates[0], coordinates[1]];
        }
        for (const child of coordinates) {
            const found = this.findFirstCoordinate(child);
            if (found) {
                return found;
            }
        }
        return null;
    }

    private clusterIntoPushpins(clusteredPushpinParts: InitialPushpinPart[], selectedCustomerArea: CustomerArea): Pushpin[] {
        if (clusteredPushpinParts.length === 0) {
            return [];
        }

        // First group by geometry type to prevent mixing points and lines
        const geometryGroups: { [geometryType: string]: InitialPushpinPart[] } = {};

        clusteredPushpinParts.forEach(partData => {
            const geometryType = partData.originalFeature.geometry.type;
            if (!geometryGroups[geometryType]) {
                geometryGroups[geometryType] = [];
            }
            geometryGroups[geometryType].push(partData);
        });

        const pushpins: Pushpin[] = [];

        // Process each geometry type separately
        Object.entries(geometryGroups).forEach(([geometryType, partsGroup]) => {
            if (partsGroup.length === 1) {
                // Single part, create pushpin directly
                const pushpin = this.createPushpinFromSinglePart(partsGroup[0], selectedCustomerArea);
                pushpins.push(pushpin);
            } else {
                // Multiple parts of same geometry type, cluster them
                const clusteredPushpins = this.clusterPartsByGeometryType(partsGroup, selectedCustomerArea);
                pushpins.push(...clusteredPushpins);
            }
        });

        return pushpins;
    }

    private clusterPartsByGeometryType(partsGroup: InitialPushpinPart[], selectedCustomerArea: CustomerArea): Pushpin[] {
        // Create GeoJSON for pushpin clustering
        const pointsForClustering = {
            type: 'FeatureCollection' as const,
            features: partsGroup.map((partData, index) => ({
                type: 'Feature' as const,
                properties: { originalIndex: index },
                geometry: {
                    type: 'Point' as const,
                    coordinates: [partData.centerCoords.lng, partData.centerCoords.lat]
                }
            }))
        };

        // Apply DBSCAN clustering with 1.5m maxDistance, minPoints = 1
        const clustered = clustersDbscan(pointsForClustering, 1.5 / 1000, {
            minPoints: 1,
            units: 'kilometers' // @turf/clusters-dbscan 7.3 negeert units en rekent altijd in km
        });

        // Group by cluster
        const clusters: { [clusterId: string]: InitialPushpinPart[] } = {};

        clustered.features.forEach(feature => {
            const originalIndex = feature.properties.originalIndex;
            const clusterId = feature.properties.cluster !== undefined ? feature.properties.cluster.toString() : 'noise';

            if (!clusters[clusterId]) {
                clusters[clusterId] = [];
            }
            clusters[clusterId].push(partsGroup[originalIndex]);
        });

        // Create pushpins for each cluster
        const pushpins: Pushpin[] = [];

        Object.values(clusters).forEach(clusterParts => {
            const pushpin = new Pushpin();
            pushpin.customer_area_id = selectedCustomerArea.id;
            pushpin.user_id = LocalStorage.getUser().id;
            pushpin.draft = false;

            // Calculate center of all parts in this cluster
            const centerCoords = this.calculateClusterCenter(clusterParts);
            pushpin.lng = centerCoords.lng;
            pushpin.lat = centerCoords.lat;

            // Use the first feature for pushpin field mappings
            const firstFeature = clusterParts[0].originalFeature;
            this.pushpinFields.forEach((appField, index) => {
                const shpProp = this.pushpinMappingsFormArray.at(index)?.value;
                if (shpProp && firstFeature.properties[shpProp] !== undefined) {
                    pushpin[appField] = firstFeature.properties[shpProp];
                }
            });

            // Handle special geometry cases based on geometry type
            this.setSpecialPushpinGeometry(pushpin, firstFeature);

            // Assign all pushpin parts to this pushpin
            pushpin.pushpin_parts = clusterParts.map(partData => partData.pushpinPart);

            pushpins.push(pushpin);
        });

        return pushpins;
    }

    private createPushpinFromSinglePart(partData: InitialPushpinPart, selectedCustomerArea: CustomerArea): Pushpin {
        const pushpin = new Pushpin();
        pushpin.customer_area_id = selectedCustomerArea.id;
        pushpin.user_id = LocalStorage.getUser().id;
        pushpin.draft = false;

        // Set coordinates
        pushpin.lng = partData.centerCoords.lng;
        pushpin.lat = partData.centerCoords.lat;

        // Map pushpin fields from the feature
        const feature = partData.originalFeature;
        this.pushpinFields.forEach((appField, index) => {
            const shpProp = this.pushpinMappingsFormArray.at(index)?.value;
            if (shpProp && feature.properties[shpProp] !== undefined) {
                pushpin[appField] = feature.properties[shpProp];
            }
        });

        // Handle special geometry cases
        this.setSpecialPushpinGeometry(pushpin, feature);

        // Assign the pushpin part
        pushpin.pushpin_parts = [partData.pushpinPart];

        return pushpin;
    }


    private calculateClusterCenter(clusterParts: InitialPushpinPart[]): { lng: number, lat: number } {
        const totalLng = clusterParts.reduce((sum, part) => sum + part.centerCoords.lng, 0);
        const totalLat = clusterParts.reduce((sum, part) => sum + part.centerCoords.lat, 0);

        return {
            lng: totalLng / clusterParts.length,
            lat: totalLat / clusterParts.length
        };
    }

    private setSpecialPushpinGeometry(pushpin: Pushpin, feature: any): void {
        if (feature.geometry.type === 'LineString') {
            const startCoords = this.convertCoordinatesIfNeeded(
                feature.geometry.coordinates[0][0],
                feature.geometry.coordinates[0][1]
            );
            const endCoords = this.convertCoordinatesIfNeeded(
                feature.geometry.coordinates[feature.geometry.coordinates.length - 1][0],
                feature.geometry.coordinates[feature.geometry.coordinates.length - 1][1]
            );

            // Een lengtemarkering heeft lat/lng als beginpunt en end_lat/end_lng als eindpunt van de snappedpoints
            pushpin.lng = startCoords.lng;
            pushpin.lat = startCoords.lat;
            pushpin.end_lng = endCoords.lng;
            pushpin.end_lat = endCoords.lat;
            pushpin.lengthmark = true;

            // Convert all snapped points
            pushpin.snappedpoints = {
                snappedPoints: feature.geometry.coordinates.map(coord =>
                    this.convertCoordinatesIfNeeded(coord[0], coord[1])
                )
            };
        }
    }

    updateFieldMapping(fieldName: string, shapefileValue: string, id: number) {
        const fieldMapping = this.fieldMappings.get(fieldName);
        if (fieldMapping) {
            fieldMapping.set(shapefileValue, id);
        }
        this.updateImportSummary();
    }

    openDataDialog(): void {
        this.dialog.open(ShapefileDataDialogComponent, {
            width: '90vw',
            maxWidth: '1200px',
            height: '80vh',
            data: {
                features: this.features,
                shapefileProperties: this.shapefileProperties
            }
        });
    }

    // Methode om automatisch veld mappings toe te passen gebaseerd op veelvoorkomende namen
    private applyAutomaticFieldMappings() {
        if (!this.shapefileProperties || this.shapefileProperties.length === 0) {
            return;
        }

        // Reset automatische mappings
        this.automaticMappings.clear();

        // Loop door alle shapefile properties
        this.shapefileProperties.forEach(shpProp => {
            const lowerShpProp = shpProp.toLowerCase().trim();
            let internalField: string | undefined;

            // Controleer eerst of de shapefile property exact overeenkomt met een intern veld
            if (this.pushpinFields.includes(shpProp)) {
                internalField = shpProp;
            } else if (this.pushpinPartFields.includes(shpProp)) {
                internalField = shpProp;
            }

            // Als geen exacte match gevonden, controleer veelvoorkomende namen mappings
            if (!internalField && this.fieldNameMappings.has(lowerShpProp)) {
                internalField = this.fieldNameMappings.get(lowerShpProp);
            }

            // Als we een internal field hebben gevonden, pas de mapping toe
            if (internalField) {
                // Vind de index van het interne veld in pushpinFields of pushpinPartFields
                let fieldIndex = this.pushpinFields.indexOf(internalField);
                let isPushpinField = true;

                if (fieldIndex === -1) {
                    fieldIndex = this.pushpinPartFields.indexOf(internalField);
                    isPushpinField = false;
                }

                if (fieldIndex !== -1) {
                    // Stel de waarde in voor het juiste form array
                    if (isPushpinField) {
                        this.pushpinMappingsFormArray.at(fieldIndex).setValue(shpProp);
                        this.automaticMappings.add(`pushpin-${fieldIndex}`);
                    } else {
                        this.pushpinPartMappingsFormArray.at(fieldIndex).setValue(shpProp);
                        this.automaticMappings.add(`pushpinpart-${fieldIndex}`);
                    }
                }
            }
        });
    }

    // Methode om automatisch waarde mappings toe te passen voor speciale velden
    private applyAutomaticValueMappings() {
        this.mappableFields.forEach(field => {
            const fieldMapping = this.fieldMappings.get(field.name);
            const items = this.fieldItemsMap.get(field.items);
            if (fieldMapping && items && items.length > 0) {
                // Loop door alle shapefile waarden in deze mapping
                fieldMapping.forEach((mappedId, shapefileValue) => {
                    // Sla over als er al een handmatige mapping is ingesteld
                    // Lege waarden nooit automatisch koppelen
                    if (mappedId !== null || shapefileValue === '') {
                        return;
                    }

                    // Afgeleide GISIB figuratie: gebruik de voorgestelde figuratie
                    const gisibCandidates = field.name === 'decoration_id' ? this.gisibDecorationCandidates.get(shapefileValue) : null;
                    if (gisibCandidates) {
                        const decoration = findDecoration(items, gisibCandidates);
                        if (decoration) {
                            fieldMapping.set(shapefileValue, decoration.id);
                        }
                        return;
                    }

                    // Zoek naar een item dat overeenkomt met de shapefile waarde
                    const matchingItem = items.find((item: any) => {
                        return field.displayProperty.some(prop => {
                            const itemValue = item[prop]?.toString().toLowerCase().trim();
                            const shpValue = this.valueMappings.get(shapefileValue?.toString().toLowerCase().trim()) ?? shapefileValue?.toString().toLowerCase().trim();
                            return itemValue === shpValue;
                        });
                    });

                    if (matchingItem) {
                        fieldMapping.set(shapefileValue, matchingItem.id);
                    }
                });
            }
        });
        this.updateAvailableDefaultFields();
    }

    private mapFieldValue(appField: string, shapefileValue: any, pushpinPart: PushpinPart): void {
        const isIdField = this.mappableFields.map(field => field.name).includes(appField);
        if (isIdField) {
            const fieldMapping = this.fieldMappings.get(appField);
            const id = fieldMapping?.get(shapefileValue);
            if (id) {
                pushpinPart[appField] = id;
            } else if (appField === 'status_id') {
                pushpinPart[appField] = 1;
            } else if (appField === 'paint_color_id') {
                console.log(`No mapping found for paint_color_id value: ${shapefileValue}`);
                pushpinPart.description = `${pushpinPart.description ?? ''}\nKleur in Shapefile: ${shapefileValue}`;
            } else if (appField === 'material_id') {
                console.log(`No mapping found for material_id value: ${shapefileValue}`);
                pushpinPart.description = `${pushpinPart.description ?? ''}\nMateriaal in Shapefile: ${shapefileValue}`;
            } else if (appField === 'decoration_id') {
                console.log(`No mapping found for decoration_id value: ${shapefileValue}`);
                pushpinPart.description = `${pushpinPart.description ?? ''}\nFiguratie in Shapefile: ${shapefileValue}`;
            }
        } else {
            pushpinPart[appField] = shapefileValue;
        }
    }

    addNewInspection = (name: string): Promise<Inspection> => {
        return new Promise((resolve, reject) => {
            const customerAreaId = this.importForm.get('customer_area_id')?.value;
            const newInspection = new Inspection();
            newInspection.name = name;
            newInspection.customer_area_id = customerAreaId;
            newInspection.inspection_type_id = 1;
            newInspection.inspection_status_id = 1;
            newInspection.user_id = LocalStorage.getUser().id;
            newInspection.lock = false;

            this.inspectionService.saveInspection(newInspection).subscribe(
                response => {
                    const savedInspection = response.data;
                    this.inspections.push(savedInspection);
                    this.importForm.get('inspection_id')?.setValue(savedInspection.id);
                    resolve(savedInspection);
                },
                error => {
                    console.error('Fout bij aanmaken nieuwe inspectie:', error);
                    this.confirmDialogService.confirm('Fout bij aanmaken inspectie', 'Fout bij aanmaken nieuwe inspectie: ' + error.message).then(() => {
                    }).catch(() => {
                    });
                    reject(error);
                }
            );
        });
    };

    // Handler voor handmatige veranderingen in mappings
    onMappingManualChange(type: 'pushpin' | 'pushpinpart', index: number) {
        // Verwijder de automatische mapping markering wanneer gebruiker handmatig verandert
        this.automaticMappings.delete(`${type}-${index}`);
        // Update available default fields
        this.updateAvailableDefaultFields();
    }

    onMappingChange(fieldName: string) {
        if (['material_id', 'decoration_id', 'status_id', 'paint_color_id'].includes(fieldName)) {
            this.initializeFieldMappings();
        }
        // Update available default fields when mappings change
        if (['material_id', 'decoration_id', 'paint_color_id'].includes(fieldName)) {
            this.updateAvailableDefaultFields();
        }
    }

    private isValidRDCoordinate(lng: number, lat: number): boolean {
        // RD coordinates are in meters, typically in the range:
        // X (lng): 12000 - 280000 (roughly)
        // Y (lat): 300000 - 620000 (roughly)
        // WGS84 coordinates are in degrees: lng: -180 to 180, lat: -90 to 90
        return (
            lng >= 12000 && lng <= 280000 && // RD X coordinate range
            lat >= 300000 && lat <= 620000 // RD Y coordinate range
        );
    }

    private fetchLocationsForPushpins(pushpins: Pushpin[], selectedCustomerArea: CustomerArea, selectedInspection: Inspection, appends: PushpinPartAppend[] = []) {
        // Maak observables voor elke pushpin locatie call
        const locationObservables = pushpins.map(pushpin =>
            this.locationService.getLocations(pushpin.lat, pushpin.lng).pipe(
                map(locations => ({pushpin, locations}))
            )
        );

        // Voer alle locatie calls sequentieel uit
        from(locationObservables).pipe(
            concatMap(obs => obs)
        ).subscribe({
            next: ({pushpin, locations}) => {
                // Vul de locatie velden voor deze pushpin
                this.fillLocationFields(pushpin, locations);
            },
            complete: () => {
                // Alle locaties zijn opgehaald, ga nu door met opslaan
                this.savePushpins(pushpins, selectedCustomerArea, selectedInspection, appends);
            },
            error: (err) => {
                console.error('Error fetching locations:', err);
                // Ga toch door met opslaan, misschien met lege locatie velden
                this.savePushpins(pushpins, selectedCustomerArea, selectedInspection, appends);
            }
        });
    }

    private fillLocationFields(pushpin: Pushpin, locations: any) {
        if (locations.resourceSets && locations.resourceSets.length > 0 && locations.resourceSets[0].resources.length > 0) {
            const address = locations.resourceSets[0].resources[0].address;
            pushpin.street = address.street || '';
            pushpin.zipcode = address.zipcode || '';
            pushpin.housenumber = address.housenumber || '';
            pushpin.place = address.locality || '';
        }
    }

    private savePushpins(pushpins: Pushpin[], selectedCustomerArea: CustomerArea, selectedInspection: Inspection, appends: PushpinPartAppend[] = []) {
        // Saved in chunks (one request and one database transaction per chunk) instead of one request per pushpin
        const chunks: { pushpins: Pushpin[], appends: PushpinPartAppend[] }[] = [];
        const items = [
            ...pushpins.map(pushpin => ({pushpin, append: null as PushpinPartAppend})),
            ...appends.map(append => ({pushpin: null as Pushpin, append}))
        ];
        for (let i = 0; i < items.length; i += this.saveChunkSize) {
            const chunk = items.slice(i, i + this.saveChunkSize);
            chunks.push({
                pushpins: chunk.filter(item => item.pushpin).map(item => item.pushpin),
                appends: chunk.filter(item => item.append).map(item => item.append)
            });
        }

        let saved = 0;
        of(...chunks).pipe(
            concatMap(chunk => this.pushpinService.saveBatch(
                selectedCustomerArea.id,
                chunk.pushpins,
                chunk.appends.map(append => ({pushpin_id: append.pushpinId, pushpin_parts: append.parts}))
            ).then(() => {
                saved += chunk.pushpins.length + chunk.appends.length;
                this.importing = saved;
            }))
        ).subscribe({
            next: () => {
            },
            complete: () => {
                this.importing = null;
                this.totalPushpins = 0;
                const points = [
                    ...pushpins.map(pushpin => turf.point([pushpin.lng, pushpin.lat])),
                    ...appends.map(append => turf.point([append.lng, append.lat]))
                ];
                const featureCollection = turf.featureCollection(points);
                const center = turf.center(featureCollection);
                const lat = center.geometry.coordinates[1];
                const lng = center.geometry.coordinates[0];
                this.router.navigate(['/',
                    'pushpins', 'map',
                    selectedCustomerArea.id, 13, lat, lng, {inspection: selectedInspection.id}]);
            },
            error: (err) => {
                console.error(err);
                const total = this.totalPushpins;
                this.importing = null;
                this.totalPushpins = 0;
                this.confirmDialogService.confirm(
                    'Fout bij importeren',
                    `Fout bij importeren: ${err?.message ?? ''}<br><br>${saved} van de ${total} punaises zijn opgeslagen. ` +
                    'Start de import opnieuw met hetzelfde bestand om de rest aan te vullen.',
                    'Sluiten', null).catch(() => {
                });
            }
        });
    }

    private convertCoordinatesIfNeeded(lng: number, lat: number): { lng: number; lat: number } {
        if (this.isValidRDCoordinate(lng, lat)) {
            try {
                const wgs84Coords = convertToWGS84(lng / 1000, lat / 1000);
                return {lng: wgs84Coords.lon, lat: wgs84Coords.lat};
            } catch (error) {
                console.error('Error converting RD to WGS84:', error);
                return {lng, lat};
            }
        }
        return {lng, lat};
    }
}
